import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../api'
import { useNavigate, useParams } from 'react-router-dom'
import TiptapEditor from '../../components/common/TiptapEditor'

interface BoardInfo {
  id: number
  slug: string
  name: string
  has_image: number
  has_file: number
}

const BoardWrite = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const [board, setBoard] = useState<BoardInfo | null>(null)
  const [loadingBoard, setLoadingBoard] = useState(true)

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!slug) return
    const fetchBoard = async () => {
      setLoadingBoard(true)
      try {
        const res = await fetchWithAuth(getApiUrl(`/marubit/api/board/${slug}`))
        if (res.ok) {
          const data = await res.json()
          setBoard(data)
        }
      } catch {
        // 게시판 정보 로드 실패 시 기본값 사용
      }
      setLoadingBoard(false)
    }
    fetchBoard()
  }, [slug])

  const handleSubmit = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return }
    if (!content || content === '<p></p>') { alert('내용을 입력해주세요.'); return }

    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('title', title)
      formData.append('content', content)
      files.forEach(file => formData.append('files', file))

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/board/${slug}/post`), {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) throw new Error('등록 실패')
      const data = await res.json()
      alert(data.message ?? '등록되었습니다.')
      navigate(`/board/${slug}`)
    } catch {
      alert('게시글 등록에 실패했습니다.')
    }
    setSubmitting(false)
  }

  if (loadingBoard) {
    return (
      <div className="bg-[#fafafa] min-h-screen flex items-center justify-center">
        <p className="text-gray-300 text-sm">불러오는 중...</p>
      </div>
    )
  }

  const hasImage = board?.has_image !== 0
  const hasFile = board?.has_file !== 0

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#887267] mb-2">
            {board?.name ?? '게시글'} 작성
          </h1>
          <p className="text-[14px] text-gray-400">내용을 입력하고 저장해주세요.</p>
        </div>

        {/* 제목 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">제목</h2>
          <input
            type="text"
            placeholder="제목을 입력해주세요 *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
          />
        </div>

        {/* 내용 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">내용</h2>
          <TiptapEditor
            onChange={setContent}
            imageUploadUrl={`/marubit/api/board/${slug}/post/admin/upload-image`}
            hasImage={hasImage}
          />
        </div>

        {/* 첨부파일 - has_file이 1일 때만 표시 */}
        {hasFile && (
          <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">첨부파일</h2>
            <p className="text-[12px] text-gray-400 ml-[13px] mb-4">최대 5개까지 첨부 가능합니다.</p>
            <input
              type="file"
              multiple
              onChange={e => {
                const selected = Array.from(e.target.files ?? [])
                const merged = [...files, ...selected]
                if (merged.length > 5) {
                  alert('첨부파일은 최대 5개까지 가능합니다.')
                  setFiles(merged.slice(0, 5))
                } else {
                  setFiles(merged)
                }
                e.target.value = ''
              }}
              className="block text-[13px] text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-[13px] file:font-semibold file:bg-[#f5f2f0] file:text-[#887267] hover:file:bg-[#e8e0dc] cursor-pointer"
            />
            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-[13px] text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-2">
                      <span className="text-[#887267]">📎</span>{f.name}
                    </span>
                    <button
                      onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                      className="text-[12px] text-red-400 hover:text-red-600 ml-4"
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[12px] text-gray-300">{files.length}/5</p>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/board/${slug}`)}
            className="flex-1 h-[52px] bg-white text-[#887267] border border-[#887267] rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-[3] h-[52px] bg-[#887267] text-white border-none rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)] disabled:opacity-50"
          >
            {submitting ? '저장 중...' : '저장'}
          </button>
        </div>

      </div>
    </div>
  )
}

export default BoardWrite